/**
 * S1 \u68cb\u76d8\u4e0e\u5339\u914d\u7cfb\u7edf \u2014\u2014 \u5b9e\u73b0\u81ea design/gdd/systems/S1-board-match.md\u3002
 *
 * \u804c\u8d23\uff1a\u68cb\u76d8\u5efa\u6a21\u3001\u5408\u6cd5\u4ea4\u6362\u5224\u5b9a\u3001\u5339\u914d\u68c0\u6d4b\u4e0e\u5f62\u72b6\u5206\u7c7b\u3001
 *       \u6b7b\u5c40\u68c0\u6d4b\u3002\u4e0d\u8d1f\u8d23\u91cd\u529b/\u8865\u6599\uff08\u90a3\u662f S2\uff09\u3002
 */

import {
  BOARD_SIZE, MIN_MATCH_LEN, ALL_COLORS,
  type Board0, type Coord, type Cell, type MatchGroup, type PieceColor, type ShapeType,
} from './types';
import { coordKey, inBoard } from './types';

/** \u521d\u59cb\u5316\u7a7a\u76d8 */
export function createEmptyBoard(): Board0 {
  const cells: Cell[][] = [];
  for (let r = 0; r < BOARD_SIZE; r++) {
    const row: Cell[] = [];
    for (let c = 0; c < BOARD_SIZE; c++) row.push({ kind: 'EMPTY' });
    cells.push(row);
  }
  return { size: BOARD_SIZE, cells };
}

/** \u53d6\u683c \u2014\u2014 \u8d8a\u754c\u8fd4\u56de null\uff08\u907f\u514d\u5927\u91cf\u8fb9\u754c\u68c0\u67e5\uff09 */
export function getCell(board: Board0, c: Coord): Cell | null {
  if (!inBoard(c)) return null;
  return board.cells[c.r]?.[c.c] ?? null;
}

/** \u8be5\u683c\u662f\u5426\u4e3a\u53ef\u53c2\u4e0e\u5339\u914d\u7684\u5b9e\u4f53 */
function isMatchable(cell: Cell | null): boolean {
  return !!cell && (cell.kind === 'PIECE' || cell.kind === 'SPECIAL');
}

/** \u53d6\u5339\u914d\u7528\u989c\u8272\uff1b\u661f\u4e91\u6838\u65e0\u8272\u8fd4\u56de null\uff08FC-2\uff09 */
function matchColor(cell: Cell | null): PieceColor | null {
  if (!cell || !isMatchable(cell)) return null;
  if (cell.special === 'NEBULA_CORE') return null;
  return cell.color ?? null;
}

/** \u540c\u8272\u4e14\u53ef\u5339\u914d */
function sameColor(a: PieceColor | null, b: PieceColor | null): boolean {
  return a !== null && a === b;
}

/**
 * S1 \u00a72.3\uff1a\u4ea4\u6362\u5408\u6cd5\u6027\u5224\u5b9a\u3002
 * \u77ed\u8def\u987a\u5e8f\uff1a\u8d8a\u754c \u2192 \u76f8\u90bb \u2192 \u4e24\u683c\u5747\u5b58\u5728 \u2192 \u81f3\u5c11\u4e00\u65b9\u53ef\u52a8 \u2192 \u6362\u540e\u80fd\u6d88\u3002
 */
export function isSwapLegal(board: Board0, from: Coord, to: Coord): boolean {
  if (!inBoard(from) || !inBoard(to)) return false;
  const d = Math.abs(from.c - to.c) + Math.abs(from.r - to.r);
  if (d !== 1) return false;

  const a = getCell(board, from);
  const b = getCell(board, to);
  if (!a || !b) return false;
  if (a.kind === 'EMPTY' || b.kind === 'EMPTY') return false;

  // \u81f3\u5c11\u4e00\u65b9\u662f\u53ef\u52a8\u7684\uff08PIECE / SPECIAL\uff09
  const movable = (x: Cell) => x.kind === 'PIECE' || x.kind === 'SPECIAL';
  if (!movable(a) && !movable(b)) return false;

  // FC-1\uff1a\u4efb\u610f\u4e00\u65b9\u542b SPECIAL \u5219\u6362\u540e\u76f4\u63a5\u5f15\u7206\uff0c\u65e0\u9700\u5148\u6210\u5339\u914d
  if (a.special || b.special) return true;

  // \u5426\u5219\u8981\u6c42\u6362\u540e\u80fd\u5f62\u6210\u2265\u4e09
  swapCells(board, from, to);
  const ok = findMatchGroups(board).length > 0;
  swapCells(board, from, to); // \u56de\u6eda
  return ok;
}

/** \u76f4\u63a5\u4ea4\u6362\u4e24\u683c\uff08\u4e0d\u505a\u4efb\u4f55\u6821\u9a8c\uff09 */
export function swapCells(board: Board0, a: Coord, b: Coord): void {
  const ca = getCell(board, a);
  const cb = getCell(board, b);
  if (!ca || !cb) return;
  board.cells[a.r][a.c] = cb;
  board.cells[b.r][b.c] = ca;
}

/**
 * S1 \u00a72.4\uff1a\u626b\u63cf\u5168\u76d8\u3002
 * \u5148\u627e\u6a2a\u5411\u8fde\u7eed\u6bb5\uff0c\u518d\u627e\u7eb5\u5411\uff0c\u7136\u540e\u5408\u5e76\u4ea4\u53c9\u4e3a L/T\uff0c
 * \u6700\u540e\u8bc6\u522b 2\u00d72 \u65b9\u5757\u3002
 */
export function findMatchGroups(board: Board0): MatchGroup[] {
  const hRuns = scanRuns(board, 'h');
  const vRuns = scanRuns(board, 'v');

  // \u5e76\u67e5\u96c6\uff1a\u5c06\u76f8\u4ea4\u7684\u6bb5\u5408\u5e76\u4e3a\u540c\u4e00\u7ec4
  const runs = [...hRuns, ...vRuns];
  const parent = runs.map((_, i) => i);
  const find = (x: number): number => (parent[x] === x ? x : (parent[x] = find(parent[x])));
  const union = (x: number, y: number) => { const rx = find(x), ry = find(y); if (rx !== ry) parent[rx] = ry; };

  const cellOwners = new Map<string, number[]>();
  runs.forEach((run, i) => {
    for (const p of run.path) {
      const k = coordKey(p);
      const arr = cellOwners.get(k);
      if (arr) arr.push(i); else cellOwners.set(k, [i]);
    }
  });
  for (const owners of cellOwners.values()) {
    for (let i = 1; i < owners.length; i++) union(owners[0], owners[i]);
  }

  const grouped = new Map<number, number[]>();
  runs.forEach((_, i) => {
    const root = find(i);
    const arr = grouped.get(root);
    if (arr) arr.push(i); else grouped.set(root, [i]);
  });

  const result: MatchGroup[] = [];
  for (const idxs of grouped.values()) {
    const cellSet = new Map<string, Coord>();
    let color: PieceColor | null = null;
    let maxLineLength = 0;
    const shapes = new Set<ShapeType>();
    let hasH = false, hasV = false;

    for (const i of idxs) {
      const run = runs[i];
      if (color === null) color = run.color; 
      maxLineLength = Math.max(maxLineLength, run.path.length);
      if (run.dir === 'h') hasH = true; else hasV = true;
      for (const p of run.path) cellSet.set(coordKey(p), p);
    }
    if (color === null) continue;

    // \u5f62\u72b6\u5224\u5b9a
    const cells = [...cellSet.values()];

    // 2\u00d72 \u65b9\u5757\uff08\u72ec\u7acb\u4e8e\u7ebf\u6bb5\u5224\u5b9a\uff09
    if (isSquareBlock(board, cells, color)) shapes.add('SQUARE');

    if (hasH && hasV && hasCorner(cells, color)) {
      shapes.add('LT');
    } else {
      if (maxLineLength >= 5) shapes.add('LINE_5+');
      else if (maxLineLength === 4) shapes.add('LINE_4');
      else if (maxLineLength === 3) shapes.add('LINE_3');
    }

    result.push({ cells, shapeHints: [...shapes], maxLineLength, color });
  }
  return result;
}

interface Run { dir: 'h' | 'v'; path: Coord[]; color: PieceColor }

function scanRuns(board: Board0, dir: 'h' | 'v'): Run[] {
  const runs: Run[] = [];
  const outer = BOARD_SIZE;
  const inner = BOARD_SIZE;

  for (let o = 0; o < outer; o++) {
    let path: Coord[] = [];
    let cur: PieceColor | null = null;

    const flush = () => {
      if (path.length >= MIN_MATCH_LEN && cur) runs.push({ dir, path, color: cur });
      path = [];
      cur = null;
    };

    for (let i = 0; i < inner; i++) {
      const c: Coord = dir === 'h' ? { c: i, r: o } : { c: o, r: i };
      const col = matchColor(getCell(board, c));
      if (col === null) { flush(); continue; }
      if (cur === null) { cur = col; path = [c]; }
      else if (sameColor(cur, col)) { path.push(c); }
      else { flush(); cur = col; path = [c]; }
    }
    flush();
  }
  return runs;
}

/** \u662f\u5426\u542b\u62d0\u89d2\uff08\u5f62\u6210 L/T\uff1a\u4e00\u683c\u540c\u65f6\u5c5e\u4e8e\u6a2a\u5411\u4e0e\u7eb5\u5411\u4e24\u81c2\uff09 */
function hasCorner(cells: Coord[], color: PieceColor): boolean {
  void cells; void color;
  // \u5df2\u5728\u4e0a\u65b9\u5e76\u67e5\u4e2d\u786e\u8ba4\u5b58\u5728\u4ea4\u53c9\uff1b\u8fd9\u91cc\u53ea\u9700\u5224\u65ad\u5165\u53c2\u7ec4\u540c\u65f6\u542b\u6a2a\u7eb5
  return true;
}

/** 2\u00d72 \u540c\u8272\u65b9\u5757\u8bc6\u522b */
function isSquareBlock(board: Board0, _cells: Coord[], color: PieceColor): boolean {
  for (let r = 0; r < BOARD_SIZE - 1; r++) {
    for (let c = 0; c < BOARD_SIZE - 1; c++) {
      const quad: Coord[] = [{ c, r }, { c: c + 1, r }, { c, r: r + 1 }, { c: c + 1, r: r + 1 }];
      if (quad.every((p) => matchColor(getCell(board, p)) === color)) return true;
    }
  }
  return false;
}

/**
 * S1 \u00a72.5\uff1a\u751f\u6210\u65e0\u521d\u59cb\u5339\u914d\u4e14\u81f3\u5c11\u6709\u4e00\u6b65\u5408\u6cd5\u4ea4\u6362\u7684\u76d8\u9762\u3002
 * \u53cc\u7ea6\u675f\uff1a\u4e0d\u5f97\u9884\u57cb\u5339\u914d\uff1b\u5fc5\u987b\u6709\u89e3\u3002
 */
export function generateBoard(rand: () => number = Math.random): Board0 {
  for (let attempt = 0; attempt < 200; attempt++) {
    const board = createFilledBoard(rand);
    if (findMatchGroups(board).length > 0) continue;
    if (!hasAnyLegalSwap(board)) continue;
    return board;
  }
  // \u6709\u754c\u5931\u8d25\uff1a\u9000\u5316\u4e3a\u968f\u673a\u94fa\u6ee1\uff08\u4e0d\u518d\u5f3a\u7ea6\u675f\uff09
  return createFilledBoard(rand);
}

function createFilledBoard(rand: () => number): Board0 {
  const cells: Cell[][] = [];
  for (let r = 0; r < BOARD_SIZE; r++) {
    const row: Cell[] = [];
    for (let c = 0; c < BOARD_SIZE; c++) {
      // \u907f\u514d\u4e09\u8fde\u9884\u57cb\uff1a\u4f18\u5148\u6392\u9664\u4f1a\u5f62\u6210\u4e09\u8fde\u7684\u989c\u8272
      const banned = new Set<PieceColor>();
      const left1 = c >= 1 ? row[c - 1]?.color : undefined;
      const left2 = c >= 2 ? row[c - 2]?.color : undefined;
      if (left1 && left2 && left1 === left2) banned.add(left1);
      const up1 = r >= 1 ? cells[r - 1][c]?.color : undefined;
      const up2 = r >= 2 ? cells[r - 2][c]?.color : undefined;
      if (up1 && up2 && up1 === up2) banned.add(up1);

      const pool = ALL_COLORS.filter((x) => !banned.has(x));
      const pick = pool[Math.floor(rand() * pool.length)] ?? ALL_COLORS[0];
      row.push({ kind: 'PIECE', color: pick });
    }
    cells.push(row);
  }
  return { size: BOARD_SIZE, cells };
}

/** S1 \u00a72.6\uff1a\u662f\u5426\u5b58\u5728\u4efb\u4f55\u4e00\u6b65\u5408\u6cd5\u4ea4\u6362\uff08O(8\u00d78\u00d74)\uff09 */
export function hasAnyLegalSwap(board: Board0): boolean {
  for (let r = 0; r < BOARD_SIZE; r++) {
    for (let c = 0; c < BOARD_SIZE; c++) {
      const from: Coord = { c, r };
      for (const to of [{ c: c + 1, r }, { c, r: r + 1 }] as Coord[]) {
        if (!inBoard(to)) continue;
        if (isSwapLegal(board, from, to)) return true;
      }
    }
  }
  return false;
}

/** \u8f6c\u4e3a\u53ef\u8bfb\u5b57\u7b26\u4e32\uff08\u8c03\u8bd5\u7528\uff09 */
export function boardToString(board: Board0): string {
  const sym: Record<PieceColor, string> = {
    mercury: 'M', venus: 'V', earth: 'E', mars: 'A', saturn: 'S', neptune: 'N',
  };
  return board.cells
    .map((row) => row.map((cell) => {
      if (cell.kind === 'EMPTY') return '.';
      if (cell.kind === 'OBSTACLE') return '#';
      if (cell.special) return '*';
      return cell.color ? sym[cell.color] : '?';
    }).join(' '))
    .join('\n');
}
